import React from "react";

const Cros3FormUI = ({ formData, setFormData, errors, readOnly = false }) => {
  
  // Helper to handle simple text/checkbox changes in sections
  const handleNestedDetailChange = (section, name, value, type = "text") => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [name]: type === "checkbox" ? value : value,
      },
    }));
  };

  // Helper to handle radio buttons response changes
  const handleResponseChange = (section, questionKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [questionKey]: value,
      },
    }));
  };

  // Reusable Question Row Component
  const QuestionRow = ({ num, text, section, questionKey, options }) => {
    const currentValue = formData[section]?.[questionKey] || "";
    return (
      <div className="py-3 border-bottom">
        <div className="row align-items-center">
          <div className="col-lg-6 col-md-12 mb-2 mb-lg-0">
            <span className="fw-semibold me-2">{num}.</span>
            <span>{text}</span>
          </div>
          <div className="col-lg-6 col-md-12">
            <div className="d-flex flex-wrap gap-3">
              {options.map((opt, i) => (
                <div className="form-check" key={i}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`${section}_${questionKey}`}
                    id={`${section}_${questionKey}_${i}`}
                    value={opt}
                    checked={currentValue === opt}
                    onChange={() => handleResponseChange(section, questionKey, opt)}
                    disabled={readOnly}
                  />
                  <label className="form-check-label text-dark small" htmlFor={`${section}_${questionKey}_${i}`}>
                    {opt}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // SATISFACTION OPTIONS
  const satisfOptions = ["Very satisfied", "Somewhat satisfied", "Somewhat dissatisfied", "Very dissatisfied"];
  // HOPE OPTIONS
  const hopeOptions = ["Believe strongly", "Believe", "Believe a little", "Do not believe"];
  // DAILY LIFE OPTIONS
  const dailyOptions = ["All the time", "Most of the time", "Sometimes", "Almost never"];
  // PROGRESS OPTIONS
  const progressOptions = ["A great deal", "Some", "Very little", "None at all"];
  // COPING OPTIONS
  const copingOptions = [
    "Very well -  never a problem",
    "Fairly well -  rarely a problem",
    "Not so well -  often a problem",
    "Very poorly - always a problem"
  ];
  const copingOptionsVip = [
    "Very well -  never a problem",
    "Fairly well - rarely a problem",
    "Not so well -  often a problem",
    "Very poorly -  always a problem"
  ];

  return (
    <div className="content p-4">
      {/* 3 Main Accordions */}
      <div className="accordion" id="mainCros3Accordion">
        
        {/* ========================================================================= */}
        {/* PART 1: CONSUMER CROS 3.0 */}
        {/* ========================================================================= */}
        <div className="accordion-item card mb-4 border shadow-sm">
          <h2 className="accordion-header" id="headingConsumerCros">
            <button
              className="accordion-button bg-label-primary text-primary fw-bold"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseConsumerCros"
              aria-expanded="true"
              aria-controls="collapseConsumerCros"
            >
              <i className="ri-user-heart-line me-2"></i> Consumer CROS 3.0 Questionnaire
            </button>
          </h2>
          <div
            id="collapseConsumerCros"
            className="accordion-collapse collapse show"
            aria-labelledby="headingConsumerCros"
            data-bs-parent="#mainCros3Accordion"
          >
            <div className="accordion-body pt-4">
              
              {/* Nested Accordion for Consumer CROS */}
              <div className="accordion" id="consumerSubAccordion">
                
                {/* 1.1 Consumer Metadata */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerMeta">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerMeta"
                      aria-expanded="false"
                      aria-controls="collapseConsumerMeta"
                    >
                      Consumer Identification & Metadata
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerMeta"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerMeta"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Month</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.month || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "month", e.target.value)}
                            disabled={readOnly}
                            placeholder="Month"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Day</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.day || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "day", e.target.value)}
                            disabled={readOnly}
                            placeholder="Day"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Year</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.year || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "year", e.target.value)}
                            disabled={readOnly}
                            placeholder="Year"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Organization ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.organization_id || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "organization_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Organization ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Agency ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.agency_id || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "agency_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Agency ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Program ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.program_id || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "program_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Program ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Staff ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.consumer_details?.staff_id || ""}
                            onChange={(e) => handleNestedDetailChange("consumer_details", "staff_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Staff ID"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1.2 Q1-Q5: Services */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerServices">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerServices"
                      aria-expanded="false"
                      aria-controls="collapseConsumerServices"
                    >
                      Q1 - Q5: Hopes & Satisfaction with Services (New clients start at Q6)
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerServices"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerServices"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={1}
                        text="How do I feel about the amount of information I receive about my mental illness?"
                        section="consumer_responses"
                        questionKey="q1"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={2}
                        text="How do I feel about the choices I get about my care?"
                        section="consumer_responses"
                        questionKey="q2"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={3}
                        text="How do I feel about the amount of activities or groups I am offered to help me cope with my mental illness?"
                        section="consumer_responses"
                        questionKey="q3"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={4}
                        text="How do I feel about the help I get finding services I need in the community?"
                        section="consumer_responses"
                        questionKey="q4"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={5}
                        text="How do I feel about the availability of crisis services when I need them?"
                        section="consumer_responses"
                        questionKey="q5"
                        options={satisfOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 1.3 Q6-Q12: Hopes for the Future */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerFuture">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerFuture"
                      aria-expanded="false"
                      aria-controls="collapseConsumerFuture"
                    >
                      Q6 - Q12: Think about your hopes for the future
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerFuture"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerFuture"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={6}
                        text="I believe I will handle my daily problems."
                        section="consumer_responses"
                        questionKey="q6"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={7}
                        text="I believe I will trust my thoughts and feelings."
                        section="consumer_responses"
                        questionKey="q7"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={8}
                        text="I believe I will feel alert and alive."
                        section="consumer_responses"
                        questionKey="q8"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={9}
                        text="I believe I will achieve goals I set for myself."
                        section="consumer_responses"
                        questionKey="q9"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={10}
                        text="I believe my life will have meaning."
                        section="consumer_responses"
                        questionKey="q10"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={11}
                        text="I believe I will have enjoyable experiences."
                        section="consumer_responses"
                        questionKey="q11"
                        options={hopeOptions}
                      />
                      <QuestionRow
                        num={12}
                        text="I believe I will recover from mental illness."
                        section="consumer_responses"
                        questionKey="q12"
                        options={hopeOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 1.4 Q13-Q20: Daily Life & Progress */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerDaily">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerDaily"
                      aria-expanded="false"
                      aria-controls="collapseConsumerDaily"
                    >
                      Q13 - Q20: Daily Life Activities & Progress
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerDaily"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerDaily"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={13}
                        text="I plan and keep a daily routine on my own."
                        section="consumer_responses"
                        questionKey="q13"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={14}
                        text="I concentrate and finish tasks that I start."
                        section="consumer_responses"
                        questionKey="q14"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={15}
                        text="I make plans to do things with other people."
                        section="consumer_responses"
                        questionKey="q15"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={16}
                        text="I manage my residence (for example, do chores, pay bills, keep my room/house clean)."
                        section="consumer_responses"
                        questionKey="q16"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={17}
                        text="I make my own decisions."
                        section="consumer_responses"
                        questionKey="q17"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={18}
                        text="I take care of my personal appearance (for example, bathe, wear clean clothes)."
                        section="consumer_responses"
                        questionKey="q18"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={19}
                        text="I am involved in meaningful activity (for example, employment, school, volunteer work)."
                        section="consumer_responses"
                        questionKey="q19"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={20}
                        text="How much progress am I making in recovering from my mental illness?"
                        section="consumer_responses"
                        questionKey="q20"
                        options={progressOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 1.5 Q21-Q26: Coping */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerCoping">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerCoping"
                      aria-expanded="false"
                      aria-controls="collapseConsumerCoping"
                    >
                      Q21 - Q26: How well am I coping?
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerCoping"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerCoping"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={21}
                        text="How well am I coping with feeling sad?"
                        section="consumer_responses"
                        questionKey="q21"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={22}
                        text="How well am I coping with feeling tense or anxious?"
                        section="consumer_responses"
                        questionKey="q22"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={23}
                        text="How well am I coping with feeling angry or hostile toward others?"
                        section="consumer_responses"
                        questionKey="q23"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={24}
                        text="How well am I coping with disturbing thoughts?"
                        section="consumer_responses"
                        questionKey="q24"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={25}
                        text="How well am I coping with difficulty sleeping?"
                        section="consumer_responses"
                        questionKey="q25"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={26}
                        text="How well am I coping with external stressors (for example, health problems, family conflicts, unemployment, tragedy in community)?"
                        section="consumer_responses"
                        questionKey="q26"
                        options={copingOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 1.6 Q27-Q35: Satisfaction */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerSatisf">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerSatisf"
                      aria-expanded="false"
                      aria-controls="collapseConsumerSatisf"
                    >
                      Q27 - Q35: Life Satisfaction
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerSatisf"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerSatisf"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={27}
                        text="How do I feel about the amount of freedom I have where I live?"
                        section="consumer_responses"
                        questionKey="q27"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={28}
                        text="How do I feel about having things to do that I enjoy (for example, going to the movies, sports events, hobbies)?"
                        section="consumer_responses"
                        questionKey="q28"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={29}
                        text="How do I feel about the way I spend my free time?"
                        section="consumer_responses"
                        questionKey="q29"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={30}
                        text="How do I feel about the amount of privacy I have where I live?"
                        section="consumer_responses"
                        questionKey="q30"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={31}
                        text="How do I feel about the amount of comfort and security I have where I live?"
                        section="consumer_responses"
                        questionKey="q31"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={32}
                        text="How do I feel about my access to health care?"
                        section="consumer_responses"
                        questionKey="q32"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={33}
                        text="How do I feel about the neighborhood and residence where I live?"
                        section="consumer_responses"
                        questionKey="q33"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={34}
                        text="How do I feel about my access to reliable transportation?"
                        section="consumer_responses"
                        questionKey="q34"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={35}
                        text="How do I feel about my relationships with family/significant others/friends?"
                        section="consumer_responses"
                        questionKey="q35"
                        options={satisfOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 1.7 Q36-Q38 & Helpers */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingConsumerOther">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsumerOther"
                      aria-expanded="false"
                      aria-controls="collapseConsumerOther"
                    >
                      Q36 - Q38: Alcohol, Drugs, Medication & Helpers
                    </button>
                  </h2>
                  <div
                    id="collapseConsumerOther"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsumerOther"
                    data-bs-parent="#consumerSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={36}
                        text="I believe my use of alcohol or street drugs interferes with my recovery from mental illness."
                        section="consumer_responses"
                        questionKey="q36"
                        options={["I do not use alcohol or street drugs.", "Believe strongly", "Believe", "Believe a little", "Do not believe"]}
                      />
                      <QuestionRow
                        num={37}
                        text="I take my psychiatric medication as prescribed."
                        section="consumer_responses"
                        questionKey="q37"
                        options={["Psychiatric medication has not been prescribed for me.", "All the time", "Most of the time", "Sometimes", "Almost never"]}
                      />
                      <QuestionRow
                        num={38}
                        text="I believe taking my psychiatric medication helps with my recovery from mental illness."
                        section="consumer_responses"
                        questionKey="q38"
                        options={["Psychiatric medication has not been prescribed for me.", "Believe strongly", "Believe", "Believe a little", "Do not believe"]}
                      />

                      <div className="mt-4 pt-3 border-top">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label text-dark fw-semibold">Did anyone help you complete this form?</label>
                            <div className="d-flex gap-3">
                              {["Yes", "No"].map((opt, i) => (
                                <div className="form-check" key={i}>
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="consumer_help"
                                    id={`consumer_help_${opt}`}
                                    value={opt}
                                    checked={formData.consumer_details?.help === opt}
                                    onChange={(e) => handleNestedDetailChange("consumer_details", "help", e.target.value)}
                                    disabled={readOnly}
                                  />
                                  <label className="form-check-label" htmlFor={`consumer_help_${opt}`}>{opt}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label text-dark fw-semibold">Was this questionnaire translated into another language for you?</label>
                            <div className="d-flex gap-3">
                              {["Yes", "No"].map((opt, i) => (
                                <div className="form-check" key={i}>
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="consumer_translated"
                                    id={`consumer_translated_${opt}`}
                                    value={opt}
                                    checked={formData.consumer_details?.translated === opt}
                                    onChange={(e) => handleNestedDetailChange("consumer_details", "translated", e.target.value)}
                                    disabled={readOnly}
                                  />
                                  <label className="form-check-label" htmlFor={`consumer_translated_${opt}`}>{opt}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PART 2: STAFF CROS 3.0 */}
        {/* ========================================================================= */}
        <div className="accordion-item card mb-4 border shadow-sm">
          <h2 className="accordion-header" id="headingStaffCros">
            <button
              className="accordion-button collapsed bg-label-primary text-primary fw-bold"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseStaffCros"
              aria-expanded="false"
              aria-controls="collapseStaffCros"
            >
              <i className="ri-user-settings-line me-2"></i> Staff CROS 3.0 Questionnaire
            </button>
          </h2>
          <div
            id="collapseStaffCros"
            className="accordion-collapse collapse"
            aria-labelledby="headingStaffCros"
            data-bs-parent="#mainCros3Accordion"
          >
            <div className="accordion-body pt-4">
              
              {/* Nested Accordion for Staff CROS */}
              <div className="accordion" id="staffSubAccordion">
                
                {/* 2.1 Staff Details & Metadata */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffMeta">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffMeta"
                      aria-expanded="false"
                      aria-controls="collapseStaffMeta"
                    >
                      Staff Assessment & Metadata
                    </button>
                  </h2>
                  <div
                    id="collapseStaffMeta"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffMeta"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Primary DSM-IV</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.staff_details?.primary || ""}
                            onChange={(e) => handleNestedDetailChange("staff_details", "primary", e.target.value)}
                            disabled={readOnly}
                            placeholder="Primary"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Secondary DSM-IV</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.staff_details?.secondary || ""}
                            onChange={(e) => handleNestedDetailChange("staff_details", "secondary", e.target.value)}
                            disabled={readOnly}
                            placeholder="Secondary"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-secondary small">Tertiary DSM-IV</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.staff_details?.tertiary || ""}
                            onChange={(e) => handleNestedDetailChange("staff_details", "tertiary", e.target.value)}
                            disabled={readOnly}
                            placeholder="Tertiary"
                          />
                        </div>
                        <div className="col-md-12">
                          <label className="form-label text-dark fw-semibold small">1. Last hospital discharge was: (Leave blank if never hospitalized)</label>
                          <div className="d-flex flex-column gap-2 mt-1">
                            {[
                              "Within last 6 months",
                              "Within 6 months to 1 year ago",
                              "Over 1 year ago"
                            ].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="staff_last_discharge"
                                  id={`staff_discharge_${i}`}
                                  value={opt}
                                  checked={formData.staff_details?.last_discharge === opt}
                                  onChange={(e) => handleNestedDetailChange("staff_details", "last_discharge", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`staff_discharge_${i}`}>
                                  {opt}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label text-dark fw-semibold small">2. Does the consumer have a representative payee?</label>
                          <div className="d-flex gap-3">
                            {["Yes", "No"].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="staff_payee"
                                  id={`staff_payee_${opt}`}
                                  value={opt}
                                  checked={formData.staff_details?.has_payee === opt}
                                  onChange={(e) => handleNestedDetailChange("staff_details", "has_payee", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`staff_payee_${opt}`}>{opt}</label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label text-dark fw-semibold small">3. Is the consumer chronically homeless?</label>
                          <div className="d-flex gap-3">
                            {["Yes", "No"].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="staff_homeless"
                                  id={`staff_homeless_${opt}`}
                                  value={opt}
                                  checked={formData.staff_details?.chronically_homeless === opt}
                                  onChange={(e) => handleNestedDetailChange("staff_details", "chronically_homeless", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`staff_homeless_${opt}`}>{opt}</label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label text-dark fw-semibold small">4. Is the consumer new to this agency?</label>
                          <div className="d-flex gap-3">
                            {["Yes", "No"].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="staff_new_to_agency"
                                  id={`staff_new_agency_${opt}`}
                                  value={opt}
                                  checked={formData.staff_details?.new_to_agency === opt}
                                  onChange={(e) => handleNestedDetailChange("staff_details", "new_to_agency", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`staff_new_agency_${opt}`}>{opt}</label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label text-dark fw-semibold small">5. Is the consumer new to this agency? (Duplicate verification)</label>
                          <div className="d-flex gap-3">
                            {["Yes", "No"].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="staff_new_to_agency_2"
                                  id={`staff_new_agency_2_${opt}`}
                                  value={opt}
                                  checked={formData.staff_details?.new_to_agency_2 === opt}
                                  onChange={(e) => handleNestedDetailChange("staff_details", "new_to_agency_2", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`staff_new_agency_2_${opt}`}>{opt}</label>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

                {/* 2.2 Q6-Q12: Hopes for Future */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffFuture">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffFuture"
                      aria-expanded="false"
                      aria-controls="collapseStaffFuture"
                    >
                      Q6 - Q12: Think about the consumer’s hopes for the future
                    </button>
                  </h2>
                  <div
                    id="collapseStaffFuture"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffFuture"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={6}
                        text="To what extent does the consumer seem to believe he/she will handle daily problems?"
                        section="staff_responses"
                        questionKey="q6"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={7}
                        text="To what extent does the consumer seem to believe he/she will trust his/her thoughts and feelings?"
                        section="staff_responses"
                        questionKey="q7"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={8}
                        text="To what extent does the consumer seem to believe he/she will feel alert and alive?"
                        section="staff_responses"
                        questionKey="q8"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={9}
                        text="To what extent does the consumer seem to believe he/she will achieve goals he/she sets?"
                        section="staff_responses"
                        questionKey="q9"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={10}
                        text="To what extent does the consumer seem to believe his/her life will have meaning?"
                        section="staff_responses"
                        questionKey="q10"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={11}
                        text="To what extent does the consumer seem to believe he/she will have enjoyable experiences?"
                        section="staff_responses"
                        questionKey="q11"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={12}
                        text="To what extent does the consumer seem to believe he/she will recover from mental illness?"
                        section="staff_responses"
                        questionKey="q12"
                        options={dailyOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 2.3 Q13-Q20: Daily Life & Progress */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffDaily">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffDaily"
                      aria-expanded="false"
                      aria-controls="collapseStaffDaily"
                    >
                      Q13 - Q20: Think about things the consumer does in daily life
                    </button>
                  </h2>
                  <div
                    id="collapseStaffDaily"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffDaily"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={13}
                        text="How often does the consumer plan and keep a daily routine on his/her own?"
                        section="staff_responses"
                        questionKey="q13"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={14}
                        text="How often does the consumer concentrate and finish tasks that he/she starts?"
                        section="staff_responses"
                        questionKey="q14"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={15}
                        text="How often does the consumer make plans to do things with other people?"
                        section="staff_responses"
                        questionKey="q15"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={16}
                        text="How often does the consumer manage his/her residence (for example, do chores, pay bills, keep room/house clean)?"
                        section="staff_responses"
                        questionKey="q16"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={17}
                        text="How often does the consumer make his/her own decisions?"
                        section="staff_responses"
                        questionKey="q17"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={18}
                        text="How often does the consumer take care of personal appearance (bathe, wear clean clothes)?"
                        section="staff_responses"
                        questionKey="q18"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={19}
                        text="How often is the consumer involved in meaningful activity (employment, school, volunteer work)?"
                        section="staff_responses"
                        questionKey="q19"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={20}
                        text="How much progress is the consumer making toward recovering from his/her mental illness?"
                        section="staff_responses"
                        questionKey="q20"
                        options={progressOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 2.4 Q21-Q26: Coping */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffCoping">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffCoping"
                      aria-expanded="false"
                      aria-controls="collapseStaffCoping"
                    >
                      Q21 - Q26: How well is the consumer coping?
                    </button>
                  </h2>
                  <div
                    id="collapseStaffCoping"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffCoping"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={21}
                        text="How well is the consumer coping with feeling sad?"
                        section="staff_responses"
                        questionKey="q21"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={22}
                        text="How well is the consumer coping with feeling tense or anxious?"
                        section="staff_responses"
                        questionKey="q22"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={23}
                        text="How well is the consumer coping with feeling angry or hostile toward others?"
                        section="staff_responses"
                        questionKey="q23"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={24}
                        text="How well is the consumer coping with disturbing thoughts?"
                        section="staff_responses"
                        questionKey="q24"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={25}
                        text="How well is the consumer coping with difficulty sleeping?"
                        section="staff_responses"
                        questionKey="q25"
                        options={copingOptions}
                      />
                      <QuestionRow
                        num={26}
                        text="How well is the consumer coping with external stressors (health problems, family conflicts, unemployment, tragedy)?"
                        section="staff_responses"
                        questionKey="q26"
                        options={copingOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 2.5 Q27-Q35: Life Satisfaction */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffSatisf">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffSatisf"
                      aria-expanded="false"
                      aria-controls="collapseStaffSatisf"
                    >
                      Q27 - Q35: How satisfied is the consumer?
                    </button>
                  </h2>
                  <div
                    id="collapseStaffSatisf"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffSatisf"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={27}
                        text="How does the consumer feel about the amount of freedom he/she has where he/she lives?"
                        section="staff_responses"
                        questionKey="q27"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={28}
                        text="How does the consumer feel about having things to do that he/she enjoys (for example, going to movies, sports, hobbies)?"
                        section="staff_responses"
                        questionKey="q28"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={29}
                        text="How does the consumer feel about the way he/she spends his/her free time?"
                        section="staff_responses"
                        questionKey="q29"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={30}
                        text="How does the consumer feel about the amount of privacy he/she has where he/she lives?"
                        section="staff_responses"
                        questionKey="q30"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={31}
                        text="How does the consumer feel about the amount of comfort and security he/she has where he/she lives?"
                        section="staff_responses"
                        questionKey="q31"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={32}
                        text="How does the consumer feel about his/her access to health care?"
                        section="staff_responses"
                        questionKey="q32"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={33}
                        text="How does the consumer feel about the neighborhood and residence where he/she lives?"
                        section="staff_responses"
                        questionKey="q33"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={34}
                        text="How does the consumer feel about his/her access to reliable transportation?"
                        section="staff_responses"
                        questionKey="q34"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={35}
                        text="How does the consumer feel about his/her relationships with family/significant others/friends?"
                        section="staff_responses"
                        questionKey="q35"
                        options={satisfOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 2.6 Q36-Q38: Alcohol, Drugs, Medication */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingStaffOther">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseStaffOther"
                      aria-expanded="false"
                      aria-controls="collapseStaffOther"
                    >
                      Q36 - Q38: Alcohol, Drugs & Medication
                    </button>
                  </h2>
                  <div
                    id="collapseStaffOther"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingStaffOther"
                    data-bs-parent="#staffSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={36}
                        text="To what extent do you believe the consumer’s use of alcohol or street drugs interferes with his/her recovery from mental illness?"
                        section="staff_responses"
                        questionKey="q36"
                        options={["The consumer does not use alcohol or street drugs.", "Strongly believe", "Believe", "Believe a little", "Do not believe"]}
                      />
                      <QuestionRow
                        num={37}
                        text="To what extent do you believe the consumer takes his/her psychiatric medication as prescribed?"
                        section="staff_responses"
                        questionKey="q37"
                        options={["Psychiatric medication has not been prescribed for the consumer.", "All the time", "Most of the time", "Sometimes", "Almost never"]}
                      />
                      <QuestionRow
                        num={38}
                        text="To what extent do you believe the consumer’s psychiatric medication helps with his/her recovery from mental illness?"
                        section="staff_responses"
                        questionKey="q38"
                        options={["Psychiatric medication has not been prescribed for the consumer.", "Strongly believe", "Believe", "Believe a little", "Do not believe"]}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PART 3: VIP CROS 3.0 */}
        {/* ========================================================================= */}
        <div className="accordion-item card mb-4 border shadow-sm">
          <h2 className="accordion-header" id="headingVipCros">
            <button
              className="accordion-button collapsed bg-label-primary text-primary fw-bold"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseVipCros"
              aria-expanded="false"
              aria-controls="collapseVipCros"
            >
              <i className="ri-user-star-line me-2"></i> VIP CROS 3.0 Questionnaire
            </button>
          </h2>
          <div
            id="collapseVipCros"
            className="accordion-collapse collapse"
            aria-labelledby="headingVipCros"
            data-bs-parent="#mainCros3Accordion"
          >
            <div className="accordion-body pt-4">
              
              {/* Nested Accordion for VIP CROS */}
              <div className="accordion" id="vipSubAccordion">
                
                {/* 3.1 VIP Details & Metadata */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipMeta">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipMeta"
                      aria-expanded="false"
                      aria-controls="collapseVipMeta"
                    >
                      VIP Respondent & Metadata
                    </button>
                  </h2>
                  <div
                    id="collapseVipMeta"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipMeta"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Consumer Name (Verification)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vip_details?.consumer_name || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "consumer_name", e.target.value)}
                            disabled={readOnly}
                            placeholder="Consumer Name"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Organization ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vip_details?.organization_id || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "organization_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Organization ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Program ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vip_details?.program_id || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "program_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Program ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Staff ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vip_details?.staff_id || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "staff_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Staff ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Agency ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vip_details?.agency_id || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "agency_id", e.target.value)}
                            disabled={readOnly}
                            placeholder="Agency ID"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-secondary small">Date CROS is Filled Out</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formData.vip_details?.filled_date || ""}
                            onChange={(e) => handleNestedDetailChange("vip_details", "filled_date", e.target.value)}
                            disabled={readOnly}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label text-dark fw-semibold small">Please indicate your relationship to the consumer:</label>
                          <div className="d-flex flex-wrap gap-3 mt-1">
                            {[
                              "Spouse/Significant Other",
                              "Other Relative",
                              "Residential Facility Staff",
                              "Parent/Step-parent",
                              "Friend",
                              "Other"
                            ].map((opt, i) => (
                              <div className="form-check" key={i}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="vip_relationship"
                                  id={`vip_rel_${i}`}
                                  value={opt}
                                  checked={formData.vip_details?.relationship === opt}
                                  onChange={(e) => handleNestedDetailChange("vip_details", "relationship", e.target.value)}
                                  disabled={readOnly}
                                />
                                <label className="form-check-label text-dark small" htmlFor={`vip_rel_${i}`}>
                                  {opt}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

                {/* 3.2 Q1-Q7: Hopes for Future */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipFuture">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipFuture"
                      aria-expanded="false"
                      aria-controls="collapseVipFuture"
                    >
                      Q1 - Q7: Think about the consumer’s hopes for the future
                    </button>
                  </h2>
                  <div
                    id="collapseVipFuture"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipFuture"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={1}
                        text="To what extent does the consumer seem to believe he/she will handle daily problems?"
                        section="vip_responses"
                        questionKey="q1"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={2}
                        text="To what extent does the consumer seem to believe he/she will trust his/her thoughts and feelings?"
                        section="vip_responses"
                        questionKey="q2"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={3}
                        text="To what extent does the consumer seem to believe he/she will feel alert and alive?"
                        section="vip_responses"
                        questionKey="q3"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={4}
                        text="To what extent does the consumer seem to believe he/she will achieve goals he/she sets?"
                        section="vip_responses"
                        questionKey="q4"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={5}
                        text="To what extent does the consumer seem to believe his/her life will have meaning?"
                        section="vip_responses"
                        questionKey="q5"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={6}
                        text="To what extent does the consumer seem to believe he/she will have enjoyable experiences?"
                        section="vip_responses"
                        questionKey="q6"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={7}
                        text="To what extent does the consumer seem to believe he/she will recover from mental illness?"
                        section="vip_responses"
                        questionKey="q7"
                        options={dailyOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 3.3 Q8-Q15: Daily Life & Progress */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipDaily">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipDaily"
                      aria-expanded="false"
                      aria-controls="collapseVipDaily"
                    >
                      Q8 - Q15: Think about things the consumer does in daily life
                    </button>
                  </h2>
                  <div
                    id="collapseVipDaily"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipDaily"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={8}
                        text="How often does the consumer plan and keep a daily routine on his/her own?"
                        section="vip_responses"
                        questionKey="q8"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={9}
                        text="How often does the consumer concentrate and finish tasks that he/she starts?"
                        section="vip_responses"
                        questionKey="q9"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={10}
                        text="How often does the consumer make plans to do things with other people?"
                        section="vip_responses"
                        questionKey="q10"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={11}
                        text="How often does the consumer manage his/her residence (for example, do chores, pay bills, keep room/house clean)?"
                        section="vip_responses"
                        questionKey="q11"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={12}
                        text="How often does the consumer make his/her own decisions?"
                        section="vip_responses"
                        questionKey="q12"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={13}
                        text="How often does the consumer take care of personal appearance (bathe, wear clean clothes)?"
                        section="vip_responses"
                        questionKey="q13"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={14}
                        text="How often is the consumer involved in meaningful activity (employment, school, volunteer work)?"
                        section="vip_responses"
                        questionKey="q14"
                        options={dailyOptions}
                      />
                      <QuestionRow
                        num={15}
                        text="How much progress is the consumer making toward recovering from his/her mental illness?"
                        section="vip_responses"
                        questionKey="q15"
                        options={progressOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 3.4 Q16-Q21: Coping */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipCoping">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipCoping"
                      aria-expanded="false"
                      aria-controls="collapseVipCoping"
                    >
                      Q16 - Q21: How well is the consumer coping?
                    </button>
                  </h2>
                  <div
                    id="collapseVipCoping"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipCoping"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={16}
                        text="How well is the consumer coping with feeling sad?"
                        section="vip_responses"
                        questionKey="q16"
                        options={copingOptionsVip}
                      />
                      <QuestionRow
                        num={17}
                        text="How well is the consumer coping with feeling tense or anxious?"
                        section="vip_responses"
                        questionKey="q17"
                        options={copingOptionsVip}
                      />
                      <QuestionRow
                        num={18}
                        text="How well is the consumer coping with feeling angry or hostile toward others?"
                        section="vip_responses"
                        questionKey="q18"
                        options={copingOptionsVip}
                      />
                      <QuestionRow
                        num={19}
                        text="How well is the consumer coping with disturbing thoughts?"
                        section="vip_responses"
                        questionKey="q19"
                        options={copingOptionsVip}
                      />
                      <QuestionRow
                        num={20}
                        text="How well is the consumer coping with difficulty sleeping?"
                        section="vip_responses"
                        questionKey="q20"
                        options={copingOptionsVip}
                      />
                      <QuestionRow
                        num={21}
                        text="How well is the consumer coping with external stressors (health problems, family conflicts, unemployment, tragedy)?"
                        section="vip_responses"
                        questionKey="q21"
                        options={copingOptionsVip}
                      />
                    </div>
                  </div>
                </div>

                {/* 3.5 Q22-Q30: Satisfaction */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipSatisf">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipSatisf"
                      aria-expanded="false"
                      aria-controls="collapseVipSatisf"
                    >
                      Q22 - Q30: How satisfied is the consumer?
                    </button>
                  </h2>
                  <div
                    id="collapseVipSatisf"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipSatisf"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={22}
                        text="How does the consumer feel about the amount of freedom he/she has where he/she lives?"
                        section="vip_responses"
                        questionKey="q22"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={23}
                        text="How does the consumer feel about having things to do that he/she enjoys (for example, going to movies, sports, hobbies)?"
                        section="vip_responses"
                        questionKey="q23"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={24}
                        text="How does the consumer feel about the way he/she spends his/her free time?"
                        section="vip_responses"
                        questionKey="q24"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={25}
                        text="How does the consumer feel about the amount of privacy he/she has where he/she lives?"
                        section="vip_responses"
                        questionKey="q25"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={26}
                        text="How does the consumer feel about the amount of comfort and security he/she has where he/she lives?"
                        section="vip_responses"
                        questionKey="q26"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={27}
                        text="How does the consumer feel about his/her access to health care?"
                        section="vip_responses"
                        questionKey="q27"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={28}
                        text="How does the consumer feel about the neighborhood and residence where he/she lives?"
                        section="vip_responses"
                        questionKey="q28"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={29}
                        text="How does the consumer feel about his/her access to reliable transportation?"
                        section="vip_responses"
                        questionKey="q29"
                        options={satisfOptions}
                      />
                      <QuestionRow
                        num={30}
                        text="How does the consumer feel about his/her relationships with family/significant others/friends?"
                        section="vip_responses"
                        questionKey="q30"
                        options={satisfOptions}
                      />
                    </div>
                  </div>
                </div>

                {/* 3.6 Q31-Q33: Alcohol, Drugs, Medication */}
                <div className="accordion-item border mb-2">
                  <h2 className="accordion-header" id="headingVipOther">
                    <button
                      className="accordion-button collapsed bg-light text-dark fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseVipOther"
                      aria-expanded="false"
                      aria-controls="collapseVipOther"
                    >
                      Q31 - Q33: Alcohol, Drugs & Medication
                    </button>
                  </h2>
                  <div
                    id="collapseVipOther"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingVipOther"
                    data-bs-parent="#vipSubAccordion"
                  >
                    <div className="accordion-body">
                      <QuestionRow
                        num={31}
                        text="To what extent do you believe the consumer’s use of alcohol or street drugs interferes with his/her recovery from mental illness?"
                        section="vip_responses"
                        questionKey="q31"
                        options={["The consumer does not use alcohol or street drugs.", "Strongly believe", "Believe", "Believe a little", "Do not believe"]}
                      />
                      <QuestionRow
                        num={32}
                        text="To what extent do you believe the consumer takes his/her psychiatric medication as prescribed?"
                        section="vip_responses"
                        questionKey="q32"
                        options={["Psychiatric medication has not been prescribed for the consumer.", "All the time", "Most of the time", "Sometimes", "Almost never"]}
                      />
                      <QuestionRow
                        num={33}
                        text="To what extent do you believe the consumer’s psychiatric medication helps with his/her recovery from mental illness?"
                        section="vip_responses"
                        questionKey="q33"
                        options={["Psychiatric medication has not been prescribed for the consumer.", "Strongly believe", "Believe", "Believe a little", "Do not believe"]}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Cros3FormUI;
